import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { reportAPI, salaryAPI } from '../lib/api';
import { formatCurrency, getMonthOptions, getCurrentMonth, formatMonth, downloadBlob } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Download, FileSpreadsheet, Users } from 'lucide-react';
import { toast } from 'sonner';

const Reports = () => {
  const [salaryReport, setSalaryReport] = useState(null);
  const [leaveReport, setLeaveReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  const monthOptions = getMonthOptions(12);
  const yearOptions = [2024, 2025, 2026].map((y) => ({ value: y, label: y.toString() }));

  const fetchSalaryReport = async () => {
    setLoading(true);
    try {
      const response = await reportAPI.getSalaryRegister(selectedMonth);
      setSalaryReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch salary report');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveReport = async () => {
    setLoading(true);
    try {
      const response = await reportAPI.getLeaveUtilization(selectedYear);
      setLeaveReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch leave report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSalary = async () => {
    try {
      const response = await salaryAPI.generateExcel(selectedMonth);
      downloadBlob(response.data, `salary_register_${selectedMonth}.xlsx`);
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  return (
    <div data-testid="reports-page">
      <Header title="Reports" subtitle="Generate and export HR reports" />

      <div className="p-8">
        <Tabs defaultValue="salary" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="salary" className="flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              Salary Register
            </TabsTrigger>
            <TabsTrigger value="leave" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Leave Utilization
            </TabsTrigger>
          </TabsList>

          {/* Salary Register Tab */}
          <TabsContent value="salary">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-48" data-testid="salary-month-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={fetchSalaryReport} data-testid="generate-salary-report-btn">
                  Generate Report
                </Button>
              </div>
              {salaryReport && (
                <Button variant="outline" onClick={handleExportSalary} data-testid="export-salary-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
              )}
            </div>

            {loading && (
              <div className="flex justify-center py-12">
                <div className="spinner w-8 h-8"></div>
              </div>
            )}

            {!loading && salaryReport && (
              <>
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="dashboard-card">
                    <p className="text-xs font-medium text-slate-500 uppercase">Employees</p>
                    <p className="text-2xl font-heading font-semibold text-slate-900 mt-1">
                      {salaryReport.summary.total_employees}
                    </p>
                  </div>
                  <div className="dashboard-card">
                    <p className="text-xs font-medium text-slate-500 uppercase">Total Gross</p>
                    <p className="text-2xl font-heading font-semibold text-slate-900 mt-1">
                      {formatCurrency(salaryReport.summary.total_gross_salary)}
                    </p>
                  </div>
                  <div className="dashboard-card">
                    <p className="text-xs font-medium text-slate-500 uppercase">Total Deductions</p>
                    <p className="text-2xl font-heading font-semibold text-red-600 mt-1">
                      {formatCurrency(salaryReport.summary.total_deductions)}
                    </p>
                  </div>
                  <div className="dashboard-card">
                    <p className="text-xs font-medium text-slate-500 uppercase">Net Payable</p>
                    <p className="text-2xl font-heading font-semibold text-emerald-600 mt-1">
                      {formatCurrency(salaryReport.summary.total_net_pay)}
                    </p>
                  </div>
                </div>

                {/* Table */}
                <div className="dashboard-card overflow-x-auto">
                  <h3 className="font-heading text-lg font-semibold mb-4">
                    Salary Register - {formatMonth(salaryReport.month)}
                  </h3>
                  <table className="data-table" data-testid="salary-report-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Basic</th>
                        <th>HRA</th>
                        <th>DA</th>
                        <th>Special</th>
                        <th>Gross</th>
                        <th>PF</th>
                        <th>ESI</th>
                        <th>TDS</th>
                        <th>Net Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaryReport.records.map((record) => (
                        <tr key={record.id}>
                          <td>
                            <div>
                              <p className="font-medium">{record.employee_name}</p>
                              <p className="text-xs text-slate-500">{record.employee_code}</p>
                            </div>
                          </td>
                          <td>{record.department}</td>
                          <td>{formatCurrency(record.basic_pay)}</td>
                          <td>{formatCurrency(record.hra)}</td>
                          <td>{formatCurrency(record.da)}</td>
                          <td>{formatCurrency(record.special_allowance)}</td>
                          <td className="font-medium">{formatCurrency(record.gross_salary)}</td>
                          <td className="text-red-600">{formatCurrency(record.pf_deduction)}</td>
                          <td className="text-red-600">{formatCurrency(record.esi_deduction)}</td>
                          <td className="text-red-600">{formatCurrency(record.tds_deduction)}</td>
                          <td className="font-medium text-emerald-600">{formatCurrency(record.net_pay)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {!loading && !salaryReport && (
              <div className="empty-state py-12 dashboard-card">
                <FileText className="w-12 h-12 text-slate-300" />
                <h3>No Report Generated</h3>
                <p>Select a month and click "Generate Report"</p>
              </div>
            )}
          </TabsContent>

          {/* Leave Utilization Tab */}
          <TabsContent value="leave">
            <div className="flex items-center gap-4 mb-6">
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger className="w-32" data-testid="leave-year-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={fetchLeaveReport} data-testid="generate-leave-report-btn">
                Generate Report
              </Button>
            </div>

            {loading && (
              <div className="flex justify-center py-12">
                <div className="spinner w-8 h-8"></div>
              </div>
            )}

            {!loading && leaveReport && (
              <div className="dashboard-card overflow-x-auto">
                <h3 className="font-heading text-lg font-semibold mb-4">
                  Leave Utilization Report - {leaveReport.year}
                </h3>
                <table className="data-table" data-testid="leave-report-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Casual (Total/Used/Balance)</th>
                      <th>Sick (Total/Used/Balance)</th>
                      <th>Earned (Total/Used/Balance)</th>
                      <th>Unpaid Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveReport.records.map((record) => (
                      <tr key={record.employee_id}>
                        <td>
                          <div>
                            <p className="font-medium">{record.employee_name}</p>
                            <p className="text-xs text-slate-500">{record.employee_code}</p>
                          </div>
                        </td>
                        <td>
                          <span className="text-slate-600">{record.casual_leave_total}</span>
                          <span className="mx-1">/</span>
                          <span className="text-red-600">{record.casual_leave_used}</span>
                          <span className="mx-1">/</span>
                          <span className="text-emerald-600 font-medium">{record.casual_leave_balance}</span>
                        </td>
                        <td>
                          <span className="text-slate-600">{record.sick_leave_total}</span>
                          <span className="mx-1">/</span>
                          <span className="text-red-600">{record.sick_leave_used}</span>
                          <span className="mx-1">/</span>
                          <span className="text-emerald-600 font-medium">{record.sick_leave_balance}</span>
                        </td>
                        <td>
                          <span className="text-slate-600">{record.earned_leave_total}</span>
                          <span className="mx-1">/</span>
                          <span className="text-red-600">{record.earned_leave_used}</span>
                          <span className="mx-1">/</span>
                          <span className="text-emerald-600 font-medium">{record.earned_leave_balance}</span>
                        </td>
                        <td>{record.unpaid_leave_used}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && !leaveReport && (
              <div className="empty-state py-12 dashboard-card">
                <Users className="w-12 h-12 text-slate-300" />
                <h3>No Report Generated</h3>
                <p>Select a year and click "Generate Report"</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Reports;
